import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { money } from "../lib/format";
import SmartImage from "../components/SmartImage";
import { EmptyState } from "../components/States";
import useSeo from "../hooks/useSeo";
import { useCheckoutEnabled } from "../lib/checkout";
import PaymentsPausedNotice from "../components/PaymentsPausedNotice";
import { MarketingOptIn } from "../components/MarketingOptIn";

const METHODS = [
  { id: "standard", label: "Standard shipping", note: "3-7 business days", price: 19.95 },
  { id: "express", label: "Express shipping", note: "1-3 business days", price: 34.95 },
  { id: "pickup", label: "Pickup in Townsville", note: "321 Ingham Rd, Garbutt", price: 0 },
];

export default function Checkout() {
  useSeo({ title: "Checkout - NQ Springs & 4x4 Accessories", noindex: true });
  const payments = useCheckoutEnabled();
  const { lines, clear } = useCart();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [method, setMethod] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address1: "", address2: "",
    suburb: "", state: "QLD", postcode: "", notes: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!lines.length) return;
    api
      .post("/cart/quote", {
        items: lines.map((l) => ({ slug: l.slug, qty: l.qty })),
        shipping_method: method,
      })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [lines, method]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    if (method !== "pickup" && (!form.address1.trim() || !form.suburb.trim() || !form.postcode.trim())) {
      toast.error("Please complete your delivery address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: lines.map((l) => ({ slug: l.slug, qty: l.qty })),
        ...form,
        shipping_method: method,
        marketing_opt_in: optIn,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
        return;
      }
      clear();
      toast.success("Order placed", { description: data.message || "We'll be in touch." });
      navigate(`/order/${data.order.id}`);
    } catch (err) {
      toast.error("Checkout failed", {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  }

  if (!payments) {
    return (
      <div className="container-nq section-nq" data-testid="checkout-page">
        <p className="eyebrow">Checkout</p>
        <h1 className="mt-2 text-3xl font-bold md:text-4xl">Ordering by phone for now</h1>
        <div className="mt-6 max-w-2xl">
          <PaymentsPausedNotice />
        </div>
      </div>
    );
  }

  if (!lines.length) {
    return (
      <div className="container-nq section-nq">
        <EmptyState
          title="Nothing to check out"
          message="Add something to your cart first."
          action={<Link to="/shop" className="btn-gold mt-3">Browse shop</Link>}
        />
      </div>
    );
  }

  const field =
    "w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-[color:var(--nq-gold)]";
  const labelCls =
    "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[color:var(--nq-stone)]";
  const bd = { borderColor: "rgba(255,255,255,0.14)" };

  return (
    <div className="container-nq section-nq" data-testid="checkout-page">
      <p className="eyebrow">Checkout</p>
      <h1 className="mt-2 text-3xl font-bold md:text-4xl">Complete your order</h1>

      <form className="mt-8 grid gap-8 lg:grid-cols-12" onSubmit={submit} data-testid="checkout-form">
        <div className="space-y-6 lg:col-span-7">
          <div className="card-nq p-6">
            <h2 className="text-lg font-semibold">Your details</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Full name *</label>
                <input value={form.name} onChange={set("name")} required className={field} style={bd} data-testid="checkout-name-input" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" value={form.email} onChange={set("email")} required className={field} style={bd} data-testid="checkout-email-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Phone</label>
                <input value={form.phone} onChange={set("phone")} className={field} style={bd} data-testid="checkout-phone-input" />
              </div>
            </div>
          </div>

          <div className="card-nq p-6">
            <h2 className="text-lg font-semibold">Delivery</h2>
            <div className="mt-4 space-y-2">
              {METHODS.map((m) => (
                <label
                  key={m.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3"
                  style={{
                    borderColor: method === m.id ? "var(--nq-gold)" : "rgba(255,255,255,0.12)",
                    background: method === m.id ? "rgba(198,162,74,0.07)" : "transparent",
                  }}
                  data-testid={`shipping-option-${m.id}`}
                >
                  <input
                    type="radio"
                    name="shipping"
                    checked={method === m.id}
                    onChange={() => setMethod(m.id)}
                    className="accent-[color:var(--nq-gold)]"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold">{m.label}</span>
                    <span className="block text-xs text-[color:var(--nq-stone)]">{m.note}</span>
                  </span>
                  <span className="font-mono text-sm gold-text">
                    {m.price === 0 ? "FREE" : money(m.price)}
                  </span>
                </label>
              ))}
            </div>

            {method !== "pickup" && (
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Street address *</label>
                  <input value={form.address1} onChange={set("address1")} className={field} style={bd} data-testid="checkout-address1-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Apartment, unit (optional)</label>
                  <input value={form.address2} onChange={set("address2")} className={field} style={bd} />
                </div>
                <div>
                  <label className={labelCls}>Suburb *</label>
                  <input value={form.suburb} onChange={set("suburb")} className={field} style={bd} data-testid="checkout-suburb-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>State</label>
                    <input value={form.state} onChange={set("state")} className={field} style={bd} />
                  </div>
                  <div>
                    <label className={labelCls}>Postcode *</label>
                    <input value={form.postcode} onChange={set("postcode")} className={field} style={bd} data-testid="checkout-postcode-input" />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className={labelCls}>Order notes</label>
              <textarea rows={3} value={form.notes} onChange={set("notes")} className={field} style={bd} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="card-nq sticky top-28 p-6">
            <h2 className="text-lg font-semibold">Order summary</h2>

            <div className="mt-4 space-y-3">
              {lines.map((l) => (
                <div key={l.slug} className="flex items-center gap-3">
                  <div className="w-14 shrink-0">
                    <SmartImage src={l.image} alt={l.name} ratio="aspect-square" fit="contain" fallbackLabel={l.name} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">{l.name}</p>
                    <p className="text-xs text-[color:var(--nq-stone)]">Qty {l.qty}</p>
                  </div>
                  <p className="font-mono text-xs">{money(l.price * l.qty)}</p>
                </div>
              ))}
            </div>

            <dl className="mt-5 space-y-2 border-t pt-4 text-sm" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <div className="flex justify-between">
                <dt className="text-[color:var(--nq-stone)]">Subtotal</dt>
                <dd className="font-mono">{money(quote?.subtotal ?? 0)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-[color:var(--nq-stone)]">Shipping</dt>
                <dd className="font-mono">
                  {(quote?.shipping ?? 0) === 0 ? "FREE" : money(quote?.shipping ?? 0)}
                </dd>
              </div>
              <div className="flex justify-between text-xs">
                <dt className="text-[color:var(--nq-stone)]">GST included</dt>
                <dd className="font-mono text-[color:var(--nq-stone)]">{money(quote?.gst ?? 0)}</dd>
              </div>
              <div
                className="flex justify-between border-t pt-2.5 text-base font-bold"
                style={{ borderColor: "rgba(255,255,255,0.08)" }}
              >
                <dt>Total</dt>
                <dd className="font-mono gold-text" data-testid="checkout-total">{money(quote?.total ?? 0)}</dd>
              </div>
            </dl>

            <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-[color:var(--nq-stone)]">
              <input
                type="checkbox"
                required
                className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--nq-gold)]"
                data-testid="checkout-terms-checkbox"
              />
              <span>
                I have read and accept the{" "}
                <Link to="/terms-of-sale" target="_blank" className="gold-text font-semibold">Terms of Sale</Link>{" "}
                and{" "}
                <Link to="/returns-refunds" target="_blank" className="gold-text font-semibold">Returns &amp; Refunds policy</Link>
                . I understand surplus stock is sold at cost price as a final sale, and that checking
                fitment is my responsibility.
              </span>
            </label>

            <MarketingOptIn id="checkout-marketing-opt-in" className="mt-3" checked={optIn} onChange={setOptIn} />

            <button
              type="submit"
              disabled={busy}
              className="btn-gold mt-5 w-full px-6 py-3 disabled:opacity-60"
              data-testid="checkout-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              {busy ? "Processing..." : "Pay securely"}
            </button>

            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[color:var(--nq-stone)]">
              <Lock className="h-3 w-3" /> Card details are handled by Stripe
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
